/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { queryCache, useQuery } from 'react-query';

import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ClearDismissedAdviceIcon from '@mui/icons-material/Replay';
import RerunIcon from '@mui/icons-material/Restore';
import { CircularProgress, Fade, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { AdviceSeverities, AdviceSeverity, AdviceSeverityColor, ClusterStates, Endpoints, Infer } from 'shared';

import { notifyReportableApiError } from '../../../../../components/notifyReportableApiError';
import { SimpleConfirmModal } from '../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { ClusterApiService } from '../../../../../services/ClusterApiService';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import {
  fetchAdvice,
  fetchDeepthinkerLatestRuntime,
  fetchDeepthinkerRunStatus,
  fetchDismissedAdvice,
} from '../../../../../utils/queryFns';
import { useFormatDate } from '../../../../../utils/useFormatDate';

type Props = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  advice: { severity: AdviceSeverity }[];
  dismissedAdvice: { id: string }[];
  latestRuntime: Infer<typeof Endpoints.GetDeepthinkerLatestRuntime>['Response']['200'];
  status: ClusterStates | undefined;
};

export const AdviceSeveritySummary = memo<Props>(
  ({ identificationData, advice, dismissedAdvice, latestRuntime, status }) => {
    const { t } = useTranslation();

    const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
    const [isOpenConfirm, setIsOpenConfirm] = useState(false);

    const { formatDate } = useFormatDate({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

    const highestSeverity = useMemo(
      () =>
        AdviceSeverities[advice.reduce((highest, cur) => Math.max(highest, AdviceSeverities.indexOf(cur.severity)), 0)],
      [advice],
    );

    const deepthinkerRunStatusQuery = useQuery(
      [fetchDeepthinkerRunStatus.key, { ...identificationData }],
      fetchDeepthinkerRunStatus.fn,
      { refetchInterval },
    );
    const deepthinkerRunStatus = useMemo(
      () => deepthinkerRunStatusQuery.data?.data.payload.status,
      [deepthinkerRunStatusQuery.data],
    );

    useEffect(() => {
      if (deepthinkerRunStatus === 'success') {
        setRefetchInterval(false);

        void Promise.all([
          queryCache.invalidateQueries([fetchAdvice.key, { ...identificationData }]),
          queryCache.invalidateQueries([fetchDismissedAdvice.key, { ...identificationData }]),
          queryCache.invalidateQueries([fetchDeepthinkerLatestRuntime.key, { ...identificationData }]),
        ]);
      }
    }, [deepthinkerRunStatus, setRefetchInterval, identificationData]);

    const rerunCheckButtonDisabled = useMemo(
      () =>
        deepthinkerRunStatus === 'running' ||
        status === ClusterStates.Importing ||
        status === ClusterStates.Updating ||
        status === ClusterStates.Creating ||
        status === ClusterStates.Unavailable ||
        status === ClusterStates.Stopping ||
        status === ClusterStates.Stopped,
      [status, deepthinkerRunStatus],
    );

    const clearDismissedAdvice = useCallback(() => {
      ClusterApiService.clearDismissedAdvice({
        organizationId: identificationData.organizationId,
        projectId: identificationData.projectId,
        clusterId: identificationData.clusterId,
      })
        .then(() =>
          Promise.all([
            queryCache.invalidateQueries([fetchAdvice.key, { ...identificationData }]),
            queryCache.invalidateQueries([fetchDismissedAdvice.key, { ...identificationData }]),
          ]),
        )
        .catch(notifyReportableApiError);
    }, [identificationData]);

    const triggerDeepthinkerRun = useCallback(async () => {
      setIsOpenConfirm(false);
      await ClusterApiService.triggerDeepthinkerRun(identificationData).then(() => setRefetchInterval(3_000));
    }, [identificationData]);

    return (
      <Stack height="100%" justifyContent="space-evenly">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography textAlign="left" data-cy="cluster-page-advice-tab-deepthinker-latest-run">
            <Trans
              i18nKey={'cluster:advice.last_check'}
              components={{
                1: <strong />,
              }}
              values={{
                datetime: !latestRuntime.timestamp
                  ? t('cluster:advice.not_found')
                  : formatDate(latestRuntime.timestamp),
              }}
            />
          </Typography>
          <Tooltip title={t('cluster:advice.rerun_check')}>
            <IconButton
              onClick={() => setIsOpenConfirm(true)}
              disabled={rerunCheckButtonDisabled}
              data-cy="cluster-page-advice-tab-trigger-deepthinker-run"
            >
              <RerunIcon />
            </IconButton>
          </Tooltip>
          <Fade in={!!deepthinkerRunStatus}>
            {deepthinkerRunStatus === 'running' ? (
              <CircularProgress size={20} data-cy="cluster-page-advice-tab-deepthinker-run-status-running" />
            ) : deepthinkerRunStatus === 'success' ? (
              <CheckIcon color="success" data-cy="cluster-page-advice-tab-deepthinker-run-status-success" />
            ) : (
              <ClearIcon color="error" data-cy="cluster-page-advice-tab-deepthinker-run-status-error" />
            )}
          </Fade>
        </div>

        {!!latestRuntime.timestamp && (
          <Typography textAlign="left">
            <Trans
              i18nKey={`cluster:advice.severitySummary.${highestSeverity}`}
              components={{
                br: <br />,
                1: (
                  <strong
                    style={{ textDecoration: 'underline', textDecorationColor: AdviceSeverityColor[highestSeverity] }}
                  />
                ),
              }}
            />
          </Typography>
        )}

        {dismissedAdvice.length ? (
          <Typography textAlign="left" display="flex" alignItems="center">
            <Trans i18nKey={`cluster:advice.ignored_advice`} values={{ count: dismissedAdvice.length }} />
            <Tooltip title={t('cluster:advice.undo_ignored_advice')}>
              <IconButton onClick={clearDismissedAdvice}>
                <ClearDismissedAdviceIcon />
              </IconButton>
            </Tooltip>
          </Typography>
        ) : null}
        <SimpleConfirmModal
          open={isOpenConfirm}
          message={t('cluster:advice.confirm_rerun_check')}
          onCancel={() => setIsOpenConfirm(false)}
          onConfirm={triggerDeepthinkerRun}
        />
      </Stack>
    );
  },
);
